import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Tag } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { useFavoriteStores } from "@/hooks/useFavoriteStores";

/**
 * PromoNotifier — slide-down notification strip pinned just under the site
 * header on every page. Auto-rotates active store promotions (Trulieve
 * specials for v1).
 *
 * Localized to the user: passes their saved ZIP and any favorited store
 * slugs to the backend so the rotation prioritizes promos within a 35-mile
 * radius and pins favorited shops to the front of the queue regardless of
 * distance. Falls back to the global feed if no nearby promos exist.
 */

const ROTATE_MS = 3000;
const SESSION_DISMISS_KEY = "budlink_promo_notifier_dismissed_v1";

export const PromoNotifier = () => {
  const { location } = useApp();
  const { favoriteSlugs } = useFavoriteStores();
  const [promos, setPromos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const touchStartY = useRef(null);

  // Respect a per-session dismiss so we don't nag the same user.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") {
        setDismissed(true);
      }
    } catch {
      // sessionStorage can throw in privacy mode / sandboxed iframes; safe to ignore
    }
  }, []);

  // Re-fetch when the user's ZIP changes (LocationGate update) or when
  // they toggle a store favorite. Always passes the most recent context
  // so the rotation reflects "promos near me + my saved shops".
  const zip = location?.zip || "";
  const favCsv = Array.from(favoriteSlugs || []).sort().join(",");

  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    const params = { limit: 30 };
    if (zip) params.zip = zip;
    if (favCsv) params.favorites = favCsv;
    api
      .get("/promos/live", { params })
      .then((r) => {
        if (cancelled) return;
        const rows = Array.isArray(r.data?.results) ? r.data.results : [];
        setPromos(rows);
        setIdx(0);
        // Tiny animation delay so the slide-in feels deliberate, not jarring
        if (rows.length > 0) {
          setTimeout(() => setOpen(true), 350);
        } else {
          setOpen(false);
        }
      })
      .catch(() => {
        // Silent — notifier just stays hidden if the feed is unreachable.
      });
    return () => {
      cancelled = true;
    };
  }, [dismissed, zip, favCsv]);

  // Auto-rotate every 3s. Pauses while the strip is closed.
  useEffect(() => {
    if (!open || promos.length <= 1) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % promos.length);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [open, promos.length]);

  const dismiss = () => {
    setOpen(false);
    setDismissed(true);
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    } catch {
      // see note above — non-fatal in private browsing
    }
  };

  // Touch handlers for swipe-up to dismiss
  const onTouchStart = (e) => {
    touchStartY.current = e.touches?.[0]?.clientY ?? null;
  };
  const onTouchEnd = (e) => {
    const start = touchStartY.current;
    const end = e.changedTouches?.[0]?.clientY ?? null;
    touchStartY.current = null;
    if (start != null && end != null && start - end > 24) {
      // 24px upward swipe ⇒ dismiss for the session
      dismiss();
    }
  };

  if (dismissed || promos.length === 0) return null;
  const promo = promos[idx % promos.length];
  if (!promo) return null;

  return (
    <div
      data-testid="promo-notifier"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className={`relative z-30 flex justify-center px-2 pt-2 pb-1 overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
        open ? "max-h-24 opacity-100" : "max-h-0 opacity-0 pointer-events-none pt-0 pb-0"
      }`}
    >
      <Link
        to={`/dispensaries/${promo.dispensary_slug}`}
        data-testid={`promo-notifier-card-${promo.id}`}
        className="pointer-events-auto w-full max-w-md flex items-center gap-3 rounded-2xl border border-white/15 bg-[#0f1714]/95 backdrop-blur-md px-3.5 py-2.5 shadow-[0_10px_30px_rgba(0,0,0,0.45)] hover:bg-[#142019]/95 transition-colors"
      >
        <div className="w-9 h-9 rounded-xl bg-[#d4a268]/15 border border-[#d4a268]/40 flex items-center justify-center shrink-0">
          <Tag className="w-4 h-4 text-[#d4a268]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#d4a268]/80 truncate">
            Sale · {promo.dispensary_name}
            {promo.dispensary_city ? ` · ${promo.dispensary_city}` : ""}
          </div>
          <div
            data-testid="promo-notifier-headline"
            className="text-sm text-white leading-snug line-clamp-1"
          >
            {promo.headline}
          </div>
        </div>
        <button
          type="button"
          data-testid="promo-notifier-dismiss"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dismiss();
          }}
          aria-label="Dismiss promotions"
          className="shrink-0 w-7 h-7 rounded-full text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </Link>
    </div>
  );
};

export default PromoNotifier;
